import { useEffect } from "react";
import type { ReactNode, SyntheticEvent } from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";
import { useEscapeKey } from "../../hooks";

const SIZES = {
  sm: "max-w-md",
  md: "max-w-lg",
  lg: "max-w-xl",
  xl: "max-w-3xl",
} as const;

type ModalSize = keyof typeof SIZES;

type ModalProps = {
  onClose: () => void;
  title?: ReactNode;
  subtitle?: ReactNode;
  headerAccessory?: ReactNode;
  size?: ModalSize;
  children: ReactNode;
  footer?: ReactNode;
  onSubmit?: (event: SyntheticEvent<HTMLFormElement>) => void;
  dismissible?: boolean;
  closeOnBackdrop?: boolean;
  hideCloseButton?: boolean;
};

export default function Modal({
  onClose,
  title,
  subtitle,
  headerAccessory,
  size = "md",
  children,
  footer,
  onSubmit,
  dismissible = true,
  closeOnBackdrop,
  hideCloseButton,
}: ModalProps) {
  const canCloseOnBackdrop = closeOnBackdrop ?? dismissible;
  const showCloseButton = !(hideCloseButton ?? !dismissible);

  useEscapeKey(onClose, dismissible);

  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, []);

  const body = (
    <>
      <div className="overflow-y-auto px-6 py-4">{children}</div>
      {footer && (
        <div className="flex items-center justify-end gap-2 border-t border-gray-100 px-6 py-4">
          {footer}
        </div>
      )}
    </>
  );

  return createPortal(
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4"
      onClick={() => canCloseOnBackdrop && onClose()}
    >
      <div
        className={`flex max-h-[90vh] w-full ${SIZES[size]} flex-col overflow-hidden rounded-xl bg-white shadow-lg`}
        onClick={(event) => event.stopPropagation()}
      >
        {(title || subtitle || showCloseButton) && (
          <div className="flex items-start justify-between gap-4 border-b border-gray-100 px-6 py-4">
            <div className="min-w-0">
              {title && (
                <div className="flex flex-wrap items-center gap-2">
                  <h2 className="text-base font-semibold leading-none text-gray-900">{title}</h2>
                  {headerAccessory}
                </div>
              )}
              {subtitle && <p className="mt-1.5 text-xs text-gray-500">{subtitle}</p>}
            </div>
            {showCloseButton && (
              <button
                type="button"
                onClick={onClose}
                aria-label="Close"
                className="shrink-0 rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
              >
                <X size={18} />
              </button>
            )}
          </div>
        )}

        {onSubmit ? (
          <form onSubmit={onSubmit} className="flex min-h-0 flex-1 flex-col">
            {body}
          </form>
        ) : (
          <div className="flex min-h-0 flex-1 flex-col">{body}</div>
        )}
      </div>
    </div>,
    document.body,
  );
}
